import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { formatJobPositionSalary, salaryLabelsFromT, shouldShowMensilita } from '@restworld/utils-common';
// Hook to format job position details for CardPrimary
export var useJobPositionDetailsPreview = function useJobPositionDetailsPreview(jobPosition) {
  var _useTranslation = useTranslation('common'),
      t = _useTranslation.t;

  return useMemo(function () {
    var _formatJobPositionSal, _occupation$label_it;

    var _ref = jobPosition || {},
        days_required_per_week_number = _ref.days_required_per_week_number,
        shift_category_var = _ref.shift_category_var,
        occupation = _ref.occupation,
        restaurant = _ref.restaurant,
        contract_type_var = _ref.contract_type_var,
        benefits = _ref.benefits,
        season_starts_at = _ref.season_starts_at,
        season_ends_at = _ref.season_ends_at,
        salary_month_number = _ref.salary_month_number,
        id = _ref.id; // Centralized formatter (amount + currency + net/gross + period, "k" for RAL).


    var salaryFormatted = (_formatJobPositionSal = formatJobPositionSalary(jobPosition, salaryLabelsFromT(t))) !== null && _formatJobPositionSal !== void 0 ? _formatJobPositionSal : undefined; // Seasonal accommodation

    var seasonalAccommodation = benefits ? !!benefits.find(function (benefit) {
      return (benefit === null || benefit === void 0 ? void 0 : benefit.benefit_var.key) === 'seasonal_accommodation';
    }) : false; // Contract term

    var contractTerm = (contract_type_var === null || contract_type_var === void 0 ? void 0 : contract_type_var.label) || '';
    if (season_starts_at) contractTerm = contractTerm.concat(" da ".concat(season_starts_at));
    if (season_starts_at && season_ends_at) contractTerm = contractTerm.concat(" a ".concat(season_ends_at));
    if (!season_starts_at && season_ends_at) contractTerm = contractTerm.concat(" fino a ".concat(season_ends_at)); // Shifts

    var shiftsSubTitle = days_required_per_week_number ? "".concat(days_required_per_week_number, " giorni a settimana") : "Giorni da definire";
    var shiftsTitle = (shift_category_var === null || shift_category_var === void 0 ? void 0 : shift_category_var.label) || 'Turni';
    return {
      jobPositionDetails: {
        companyName: restaurant === null || restaurant === void 0 ? void 0 : restaurant.restaurant_name,
        companyLogo: restaurant === null || restaurant === void 0 ? void 0 : restaurant.restaurant_logo,
        title: occupation === null || occupation === void 0 ? void 0 : (_occupation$label_it = occupation.label_it) === null || _occupation$label_it === void 0 ? void 0 : _occupation$label_it.label,
        jobLocation: restaurant === null || restaurant === void 0 ? void 0 : restaurant.address,
        jobLocality: restaurant === null || restaurant === void 0 ? void 0 : restaurant.locality,
        salary: salaryFormatted,
        contractTerm: contractTerm,
        shifts: {
          title: shiftsTitle,
          subTitle: shiftsSubTitle
        },
        seasonalAccommodation: seasonalAccommodation,
        // Mensilità chip only for monthly pay at an Italian venue (RAL / hourly /
        // foreign jobs must not show "per N mensilità").
        salaryMonthNumber: shouldShowMensilita(jobPosition) && salary_month_number ? salary_month_number : undefined,
        id: id
      },
      showRestaurant: jobPosition === null || jobPosition === void 0 ? void 0 : jobPosition.show_restaurant_details,
      publishedAt: jobPosition === null || jobPosition === void 0 ? void 0 : jobPosition.published_at
    };
  }, [jobPosition, t]);
}; // Hook for the Performance block data

export var usePerformanceBlockData = function usePerformanceBlockData(jobPosition) {
  var formatSalaryRating = function formatSalaryRating(rating) {
    var translations = {
      1: 'Retribuzione bassa',
      2: 'Retribuzione sotto la media',
      3: 'Retribuzione nella media',
      4: 'Retribuzione sopra la media',
      5: 'Retribuzione alta'
    };
    return translations[rating] || '';
  };

  var formatCareerRating = function formatCareerRating(rating) {
    var translations = {
      1: 'Basse possibilità di carriera',
      2: 'Possibilità di carriera sotto la media',
      3: 'Possibilità di carriera nella media',
      4: 'Possibilità di carriera sopra la media',
      5: 'Ottime possibilità di carriera'
    };
    return translations[rating] || '';
  };

  var formatWorklifeBalanceRating = function formatWorklifeBalanceRating(rating) {
    var translations = {
      1: 'Equilibrio vita-lavoro basso',
      2: 'Equilibrio vita-lavoro sotto la media',
      3: 'Equilibrio vita-lavoro nella media',
      4: 'Equilibrio vita-lavoro sopra la media',
      5: 'Ottimo equilibrio vita-lavoro'
    };
    return translations[rating] || '';
  };

  return useMemo(function () {
    var _jobPosition$assigned;

    var items = []; // CSM Insights

    if (jobPosition !== null && jobPosition !== void 0 && jobPosition.csm_insights && jobPosition !== null && jobPosition !== void 0 && (_jobPosition$assigned = jobPosition.assigned_to_user) !== null && _jobPosition$assigned !== void 0 && _jobPosition$assigned.name) {
      items.push({
        key: 'recruiter-profile',
        icon: jobPosition.assigned_to_user.icon_url || '',
        rating: 1,
        title: "Cosa ne pensa ".concat(jobPosition.assigned_to_user.name.trim(), ", Senior Recruiter di Restworld"),
        subTitle: jobPosition.csm_insights
      });
    } // Salary Rating


    if (jobPosition !== null && jobPosition !== void 0 && jobPosition.salary_rating) {
      items.push({
        key: 'pay',
        icon: 'rating-currency-icon',
        rating: jobPosition.salary_rating,
        title: 'Stipendio',
        subTitle: formatSalaryRating(jobPosition.salary_rating)
      });
    } // Career Rating


    if (jobPosition !== null && jobPosition !== void 0 && jobPosition.career_rating) {
      items.push({
        key: 'career-possibilities',
        icon: 'rating-graph-icon',
        rating: jobPosition.career_rating,
        title: 'Possibilità di carriera',
        subTitle: jobPosition.career_development_description || formatCareerRating(jobPosition.career_rating),
        hasChild: true
      });
    } // Work-life Balance Rating


    if (jobPosition !== null && jobPosition !== void 0 && jobPosition.worklife_balance_rating) {
      items.push({
        key: 'worklife-balance',
        icon: 'rating-clock-icon',
        rating: jobPosition.worklife_balance_rating,
        title: 'Equilibrio vita-lavoro',
        subTitle: formatWorklifeBalanceRating(jobPosition.worklife_balance_rating)
      });
    }

    return items;
  }, [jobPosition]);
}; // Hook for the Skills block data

export var useSkillsBlockData = function useSkillsBlockData(jobPosition) {
  return useMemo(function () {
    try {
      var _jobPosition$certific, _jobPosition$educatio, _jobPosition$required, _jobPosition$other_re, _jobPosition$language, _jobPosition$optional;

      var certificates = (jobPosition === null || jobPosition === void 0 ? void 0 : (_jobPosition$certific = jobPosition.certificates) === null || _jobPosition$certific === void 0 ? void 0 : _jobPosition$certific.map(function (_ref2) {
        var certificate_var = _ref2.certificate_var;
        return certificate_var.label;
      })) || [];
      var education = (jobPosition === null || jobPosition === void 0 ? void 0 : (_jobPosition$educatio = jobPosition.education) === null || _jobPosition$educatio === void 0 ? void 0 : _jobPosition$educatio.map(function (_ref3) {
        var education_var = _ref3.education_var;
        return education_var.label;
      })) || [];
      var requiredSkills = (jobPosition === null || jobPosition === void 0 ? void 0 : (_jobPosition$required = jobPosition.required_skills) === null || _jobPosition$required === void 0 ? void 0 : _jobPosition$required.map(function (_ref4) {
        var _label_it$id;

        var label_it = _ref4.label_it;
        return {
          key: label_it === null || label_it === void 0 ? void 0 : label_it.label,
          label: label_it === null || label_it === void 0 ? void 0 : label_it.label,
          id: label_it === null || label_it === void 0 ? void 0 : (_label_it$id = label_it.id) === null || _label_it$id === void 0 ? void 0 : _label_it$id.toString()
        };
      })) || [];
      var otherRequirements = (jobPosition === null || jobPosition === void 0 ? void 0 : (_jobPosition$other_re = jobPosition.other_requirements) === null || _jobPosition$other_re === void 0 ? void 0 : _jobPosition$other_re.map(function (_ref5) {
        var other_requirement_var = _ref5.other_requirement_var;
        return other_requirement_var.label;
      })) || [];
      var languages = (jobPosition === null || jobPosition === void 0 ? void 0 : (_jobPosition$language = jobPosition.languages) === null || _jobPosition$language === void 0 ? void 0 : _jobPosition$language.map(function (_ref6) {
        var language_var = _ref6.language_var,
            level_var = _ref6.level_var;
        return {
          key: (language_var === null || language_var === void 0 ? void 0 : language_var.key) || '',
          label: (language_var === null || language_var === void 0 ? void 0 : language_var.label) || '',
          level: (level_var === null || level_var === void 0 ? void 0 : level_var.label) || ''
        };
      })) || [];
      var optionalSkills = (jobPosition === null || jobPosition === void 0 ? void 0 : (_jobPosition$optional = jobPosition.optional_skills) === null || _jobPosition$optional === void 0 ? void 0 : _jobPosition$optional.map(function (_ref7) {
        var label_it = _ref7.label_it;
        return label_it === null || label_it === void 0 ? void 0 : label_it.label;
      })) || [];
      var isEmptyData = Boolean(!(requiredSkills !== null && requiredSkills !== void 0 && requiredSkills.length) && !(optionalSkills !== null && optionalSkills !== void 0 && optionalSkills.length) && !(otherRequirements !== null && otherRequirements !== void 0 && otherRequirements.length) && !(education !== null && education !== void 0 && education.length) && !(certificates !== null && certificates !== void 0 && certificates.length) && !(languages !== null && languages !== void 0 && languages.length));
      return {
        requiredSkills: requiredSkills,
        optionalSkills: optionalSkills,
        otherRequirements: otherRequirements,
        education: education,
        certificates: certificates,
        languages: languages,
        isEmptyData: isEmptyData
      };
    } catch (error) {
      // Error in getting skill data
      return {
        otherRequirements: [],
        education: [],
        certificates: [],
        languages: [],
        requiredSkills: [],
        optionalSkills: [],
        isEmptyData: true
      };
    }
  }, [jobPosition]);
};